import { UserEmployment } from '../atomic/user/types';
import { EMPLOYMENT_INDUSTRY_MAP, EMPLOYMENT_ROLE_MAP } from '../employment';
import {
    UsaRegionCode,
    usaRegionCodes,
    UsaRegionName,
    usaRegionNameByRegionCode,
} from '../location';
import { IconName } from '../react/icons';
import {
    AtomicDownturnReaction,
    AtomicInvestmentPriority,
    AvoidIndustry,
    SupportedValue,
} from '../types';
import { toTitleCase } from '../util/strings';
import { convertUnionToArray } from '../util/typebox';

export const labelByAvoidIndustry: Record<AvoidIndustry, string> = {
    TOBACCO: 'Tobacco',
    PRIVATE_PRISONS: 'For-profit prisons',
    ANIMAL_TESTING: 'Animal testing',
    WEAPONS: 'Weapons',
    GAMBLING: 'Gambling',
    PREDATORY_LENDING: 'Predatory lending',
};

export const AVOID_INDUSTRY_OPTIONS: PersonalizedInvestmentOption<AvoidIndustry>[] =
    [
        {
            value: 'TOBACCO',
            label: labelByAvoidIndustry['TOBACCO'],
            iconName: 'Tobacco',
            paragraphs: [
                'Exclude companies like Philip Morris and British Tobacco Company that profit from the manufacture or distribution of tobacco products.',
            ],
        },
        {
            value: 'PRIVATE_PRISONS',
            label: labelByAvoidIndustry['PRIVATE_PRISONS'],
            iconName: 'Prison',
            paragraphs: [
                'Exclude companies like CoreCivic and the GEO Group that profit from, operate, or provide staff and services for for-profit prisons and detention facilities. ',
            ],
        },
        {
            value: 'ANIMAL_TESTING',
            label: labelByAvoidIndustry['ANIMAL_TESTING'],
            iconName: 'Beaker02',
            paragraphs: [
                'Exclude companies like L’Oreal that perform animal testing for pharmaceutical and non-pharmaceutical purposes.',
            ],
        },
        {
            value: 'WEAPONS',
            label: labelByAvoidIndustry['WEAPONS'],
            iconName: 'Firearms',
            paragraphs: [
                'Exclude companies like Lockheed Martin that profit from the manufacture or distribution of military weapons and civilian firearms.',
            ],
        },
        {
            value: 'GAMBLING',
            label: labelByAvoidIndustry['GAMBLING'],
            iconName: 'CoinsStacked03',
            paragraphs: [
                'Exclude companies like DraftKings or casinos that profit from gambling or activities related to gambling.',
            ],
        },
        {
            value: 'PREDATORY_LENDING',
            label: labelByAvoidIndustry['PREDATORY_LENDING'],
            iconName: 'CreditCard02',
            paragraphs: [
                'Exclude companies that profit from loans that mislead, overcharge, or otherwise target vulnerable and low-income people.',
            ],
        },
    ];

export const CONFLICT_OF_INTEREST_OPTIONS = [
    {
        label: 'I control a public company',
        description: '10% shareholder, officer, or executive',
        value: 'yes1',
    },
    {
        label: 'I am an affiliated person',
        description: 'Employee at FINRA broker-dealer',
        value: 'yes2',
    },
    {
        label: 'I am a politician',
        description: 'Or have a family member in politics',
        value: 'yes3',
    },
    {
        label: 'None of these apply to me',
        description: 'Most individuals select this option',
        value: 'no',
    },
];

export const labelByDownturnReaction: Record<AtomicDownturnReaction, string> = {
    SELL: 'I would sell off my stocks',
    HOLD: 'I would wait and see what happens',
    BUY: 'I would buy more',
};

export const DOWNTURN_REACTION_OPTIONS: {
    value: AtomicDownturnReaction;
    label: string;
    description: string;
    rightImageName: 'DownturnChart';
}[] = [
    {
        value: 'SELL',
        label: labelByDownturnReaction['SELL'],
        description: 'I’m okay with lower returns',
        rightImageName: 'DownturnChart',
    },
    {
        value: 'HOLD',
        label: labelByDownturnReaction['HOLD'],
        description: 'I’m okay with moderate returns',
        rightImageName: 'DownturnChart',
    },
    {
        value: 'BUY',
        label: labelByDownturnReaction['BUY'],
        description: 'I’m okay with higher risk',
        rightImageName: 'DownturnChart',
    },
];

export const EMPLOYMENT_INDUSTRY_OPTIONS = Object.entries(
    EMPLOYMENT_INDUSTRY_MAP
).map(([value, label]) => ({ value, label }));

export const EMPLOYMENT_ROLE_OPTIONS = Object.entries(EMPLOYMENT_ROLE_MAP).map(
    ([value, label]) => ({ value, label })
);

export const EMPLOYMENT_STATUS_OPTIONS = convertUnionToArray(
    UserEmployment.properties.status
).map((value) => ({
    value,
    label: toTitleCase(value.replace('_', ' ')),
}));

export const labelByInvestmentPriority: Record<
    AtomicInvestmentPriority,
    string
> = {
    AVOID_LOSSES: 'Minimize losses',
    BALANCE_RISK: 'Balance risk',
    MAXIMIZE_RETURNS: 'Maximize returns',
};

export const INVESTMENT_PRIORITY_OPTIONS: {
    value: AtomicInvestmentPriority;
    label: string;
    description: string;
    rightImageName?:
        | 'MinimizeLossesChart'
        | 'BalanceRiskChart'
        | 'MaximizeReturnsChart';
}[] = [
    {
        value: 'AVOID_LOSSES',
        label: labelByInvestmentPriority['AVOID_LOSSES'],
        description: 'I’m okay with lower returns',
        rightImageName: 'MinimizeLossesChart',
    },
    {
        value: 'BALANCE_RISK',
        label: labelByInvestmentPriority['BALANCE_RISK'],
        description: 'I’m okay with moderate returns',
        rightImageName: 'BalanceRiskChart',
    },
    {
        value: 'MAXIMIZE_RETURNS',
        label: labelByInvestmentPriority['MAXIMIZE_RETURNS'],
        description: 'I’m okay with higher risk',
        rightImageName: 'MaximizeReturnsChart',
    },
];

export type PersonalizedInvestmentOption<T> = {
    value: T;
    label: string;
    iconName: IconName;
    paragraphs: string[];
};

export const labelBySupportedValue: Record<SupportedValue, string> = {
    END_POVERTY_HUNGER: 'End poverty & hunger',
    ELIMINATE_INEQUALITY: 'Eliminate inequality',
    EMPOWER_WOMEN: 'Empower women',
    PROTECT_NATURE: 'Protect nature',
    FIGHT_CLIMATE_CHANGE: 'Fight climate change',
};

export const SUPPORTED_VALUES_OPTIONS: PersonalizedInvestmentOption<SupportedValue>[] =
    [
        {
            value: 'END_POVERTY_HUNGER',
            label: labelBySupportedValue['END_POVERTY_HUNGER'],
            iconName: 'CoinsHand',
            paragraphs: [
                'You can invest in companies working to eradicate hunger and poverty provide access to resources,  emergency food in crisis areas, increase economic opportunities, and accelerate investment in impoverished areas. ',
                'Your portfolio might include stocks like NextEra Energy, an American energy company that designs programs for vulnerable people; Chubb, an insurance company that provides micro insurance for low-income households; or Pfizer, a pharmaceutical company that researches cures for deadly tropical diseases that disproportionately affect impoverished people.',
            ],
        },
        {
            value: 'ELIMINATE_INEQUALITY',
            label: labelBySupportedValue['ELIMINATE_INEQUALITY'],
            iconName: 'Scales01',
            paragraphs: [
                'You can invest in companies working to reduce inequality offer wage equality, worker opportunities, and diverse hiring practices. ',
                'Your portfolio might include stocks like Honeywell or John Deere. These companies are known for ensuring their supply chains protect workers and enforce labor standards.',
            ],
        },
        {
            value: 'EMPOWER_WOMEN',
            label: labelBySupportedValue['EMPOWER_WOMEN'],
            iconName: 'FemaleSign',
            paragraphs: [
                'You can invest in companies working to promote the rights of women and girls around the world and to assure gender equality for all.',
                'Your portfolio might include stocks like Citigroup, a bank that offers flexible maternity and dependent care leave to its employees; Workday,Inc., a software company that prioritizes gender equity in hiring; or AbbVie, a pharmaceutical company that develops contraceptives.',
            ],
        },
        {
            value: 'PROTECT_NATURE',
            label: labelBySupportedValue['PROTECT_NATURE'],
            iconName: 'Image05',
            paragraphs: [
                'You can invest in companies working to protect life on land and ensure healthier oceans and marine environments. ',
                'Your portfolio might include stocks like GFL Environmental, a company that removes contaminants from soil and Regeneron, a pharmaceutical company developing hazardous waste management.',
            ],
        },
        {
            value: 'FIGHT_CLIMATE_CHANGE',
            label: labelBySupportedValue['FIGHT_CLIMATE_CHANGE'],
            iconName: 'Wind03',
            paragraphs: [
                'You can invest in companies working to mitigate the effects of climate change. These companies work to reduce emissions, use recycled materials, promote conservation, and/or develop technologies to reduce global warming. ',
                'Your portfolio might include stocks like CCRC Corporation, a company that builds wind turbines and electric vehicles for public transportation; Delta Electronics, a company that manufactures charging solutions for electric vehicles; or Fortum, an alternative energy company developing biomass energy generation.',
            ],
        },
    ];

export const TAX_RESIDENT_OPTIONS = [
    { value: 'RESIDENT', label: 'Tax Resident' },
    { value: 'NON_RESIDENT', label: 'Not a tax resident' },
];

type UsaStateOption = {
    value: UsaRegionCode;
    label: UsaRegionName;
};

export const USA_STATES_OPTIONS: UsaStateOption[] = usaRegionCodes.map(
    (code) => ({
        value: code,
        label: usaRegionNameByRegionCode[code],
    })
);
